import { BAOCustomElement } from '@bao/custom-element'
import { eventBus, EVENTS } from '@bao/events'
import { sectionRenderer } from '@bao/section-rendering'

export class CartPage extends BAOCustomElement() {
  _loading = false

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    this.classList.toggle(this.loadingClass, val)
    this.setAttribute('aria-busy', val)
  }

  constructor () {
    super()

    this.sections = sectionRenderer.cartSections
  }

  setupListeners () {
    super.setupListeners()

    this.onBeforeCartAdd = this.onBeforeCartAdd.bind(this)
    this.onBeforeClearCartAttributes =
      this.onBeforeClearCartAttributes.bind(this)
    this.onCartChanged = this.onCartChanged.bind(this)
    this.onAfterCartAdd = this.onAfterCartAdd.bind(this)

    /* Before events */
    eventBus.addEventListener(
      EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )
    eventBus.addEventListener(
      EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    )
    eventBus.addEventListener(
      EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    )
    eventBus.addEventListener(
      EVENTS.PUSH_CART.BEFORE_CLEAR_CART_ATTRIBUTES,
      this.onBeforeClearCartAttributes,
    )

    /* Changed event */
    eventBus.addEventListener(EVENTS.PUSH_CART.CHANGED, this.onCartChanged)

    /* After events */
    eventBus.addEventListener(EVENTS.PUSH_CART.AFTER_ADD, this.onAfterCartAdd)
    eventBus.addEventListener(
      EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    )
    eventBus.addEventListener(
      EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )
    eventBus.addEventListener(
      EVENTS.PUSH_CART.AFTER_CLEAR_CART_ATTRIBUTES,
      this.onAfterCartAdd,
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    /* Before events */
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.BEFORE_CLEAR_CART_ATTRIBUTES,
      this.onBeforeClearCartAttributes,
    )

    /* Changed event */
    eventBus.removeEventListener(EVENTS.PUSH_CART.CHANGED, this.onCartChanged)

    /* After events */
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.AFTER_CLEAR_CART_ATTRIBUTES,
      this.onAfterCartAdd,
    )
  }

  onBeforeCartAdd () {
    window.drawers.activeDrawerKey = ''

    this.loading = true

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: prefersReducedMotion ? 'instant' : 'smooth',
    })
  }

  onBeforeClearCartAttributes () {
    this.loading = true
  }

  onCartChanged (e) {
    const { state } = e.detail
    if (!state) return
    if (!state.sections) return

    if (!('push-cart-count' in state.sections)) return

    const newCartTotal = parseInt(
      this.getCount(state.sections['push-cart-count']),
    )

    this.externalEls.section?.element.classList.toggle(
      'crt-Page-empty',
      newCartTotal === 0,
    )

    if (newCartTotal === 0) {
      this.externalEls.checkout?.element.setAttribute(
        'disabled',
        newCartTotal === 0,
      )
    } else {
      this.externalEls.checkout?.element.removeAttribute('disabled')
    }
  }

  onAfterCartAdd (event) {
    if (event.detail.state?.sections) {
      this.renderSections(event.detail.state.sections)
    }

    this.loading = false
  }

  /**
   * @param {string} html
   *
   * @returns {string}
   */
  getCount (html) {
    const DOM = sectionRenderer.convertToDOM(html)

    return DOM.querySelector('#push-cart-count').textContent
  }

  handleError () {
    this.loading = false
  }

  get loadingClass () {
    return this.getAttribute('loading-class')
  }
}

if (!customElements.get('cart-page')) {
  customElements.define('cart-page', CartPage)
}
